import { WIDGET_HOME_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Card, CardBody, CardHeader, CardTitle, EmptyState, Icon } from "@sagot/ui";
import { useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { emptyOption, newOptionId, reorder, type HomeOptionForm } from "./home-model";
import { HomeOptionDialog } from "./home-option-dialog";
import { HomeOptionRow } from "./home-option-row";
import type { HomeChoices } from "./use-home-data";
import type { WidgetForm } from "./widget-model";

const K = "deployKnowledge.widgetStudio.home";
const MAX = WIDGET_HOME_LIMITS.options;

/** `null` = adding; a number = editing that row. */
type Editing = { index: number | null } | null;

interface HomeOptionsProps {
  agentId: string;
  workflows: HomeChoices;
  bookings: HomeChoices;
}

/** The option list: add (up to 6), edit in a dialog, reorder with buttons, remove. */
export function HomeOptions({ agentId, workflows, bookings }: HomeOptionsProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<WidgetForm>();
  const { field } = useController({ control, name: "home.options" });
  const options = field.value;
  const [editing, setEditing] = useState<Editing>(null);
  const [announcement, setAnnouncement] = useState("");
  const names = new Map([...workflows.names, ...bookings.names]);
  const full = options.length >= MAX;

  const move = (index: number, delta: -1 | 1) => {
    const target = options[index];
    if (!target) return;
    field.onChange(reorder(options, index, index + delta));
    setAnnouncement(t(`${K}.options.moved`, { label: target.label, position: index + delta + 1, count: options.length }));
  };
  const remove = (index: number) => {
    const target = options[index];
    if (!target) return;
    field.onChange(options.filter((_, i) => i !== index));
    setAnnouncement(t(`${K}.options.removed`, { label: target.label }));
  };
  const save = (saved: HomeOptionForm) => {
    const index = editing?.index ?? null;
    if (index === null) field.onChange([...options, { ...saved, id: newOptionId(saved.label, options.map((o) => o.id)) }]);
    else field.onChange(options.map((option, i) => (i === index ? { ...saved, id: option.id } : option)));
    setAnnouncement(t(`${K}.options.saved`));
    setEditing(null);
  };

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>
            {t(`${K}.options.title`)} <span className="font-normal text-fg-subtle">{t(`${K}.options.count`, { count: options.length, max: MAX })}</span>
          </CardTitle>
          <p className="text-xs text-fg-subtle">{t(`${K}.options.hint`)}</p>
        </div>
        <Button variant="secondary" size="sm" disabled={full} onClick={() => { setEditing({ index: null }); }}>
          <Icon name="plus" />
          {t(`${K}.options.add`)}
        </Button>
      </CardHeader>
      {options.length > 0 ? (
        <ul aria-label={t(`${K}.options.listLabel`)} className="divide-y divide-line">
          {options.map((option, index) => (
            <HomeOptionRow
              key={option.id} option={option} index={index} count={options.length} names={names}
              onEdit={() => { setEditing({ index }); }} onMove={(delta) => { move(index, delta); }} onRemove={() => { remove(index); }}
            />
          ))}
        </ul>
      ) : (
        <CardBody>
          <EmptyState
            icon="message-circle" headingLevel={3} title={t(`${K}.options.emptyTitle`)} description={t(`${K}.options.emptyBody`, { max: MAX })}
            action={
              <Button size="sm" onClick={() => { setEditing({ index: null }); }}>
                <Icon name="plus" />
                {t(`${K}.options.add`)}
              </Button>
            }
          />
        </CardBody>
      )}
      {full ? <p className="border-t border-line px-5 py-3 text-xs text-fg-subtle">{t(`${K}.options.full`, { max: MAX })}</p> : null}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
      {editing ? (
        <HomeOptionDialog
          initial={editing.index === null ? emptyOption() : (options[editing.index] ?? emptyOption())} isNew={editing.index === null}
          agentId={agentId} workflows={workflows} bookings={bookings} onSave={save} onClose={() => { setEditing(null); }}
        />
      ) : null}
    </Card>
  );
}
